import { NeighbourhoodData } from "../types";
import { NeighbourhoodPayload } from "../payloadTypes";
import { toTrimmedString } from "./neighbourhoodHelpers";
import GETTING_AROUND_DATA from "../components/gettingAroundConfig";
import { transformCostOfLiving } from "./transformCostOfLiving";
import { transformSafety } from "./transformSafety";
import { transformCommunity, selectUniversities } from "./transformCommunity";
import { transformClimate } from "./transformClimate";

const TAB_KEYS = ["cost_of_living", "safety", "community", "climate"] as const;

/**
 * Narrows an unknown value to a NeighbourhoodPayload or returns null.
 *
 * Rejects null, non-objects, arrays, and objects that contain none of the
 * four tab keys (cost_of_living, safety, community, climate) by presence
 * rather than value (Requirement 1.5).
 */
const narrowPayload = (value: unknown): NeighbourhoodPayload | null => {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return null;
  const candidate = value as Record<string, unknown>;
  const hasAnyTabKey = TAB_KEYS.some((key) => key in candidate);
  return hasAnyTabKey ? (candidate as NeighbourhoodPayload) : null;
};

/**
 * Builds a NeighbourhoodData view-model from the raw backend payload and
 * the property's nearby_distances array.
 *
 * Returns null when the payload is absent or invalid — that single signal
 * tells NeighbourhoodSection to render no DOM node.
 *
 * Requirements: 1.3, 1.4, 1.5, 1.8, 1.9, 10.1, 10.2, 10.3, 10.4, 10.7, 10.8, 11.4, 11.5
 */
export const buildNeighbourhoodData = (
  rawPayload: unknown,
  rawNearbyDistances: unknown,
  countryCode?: string,
  rentBudgetPercent?: number | null,
): NeighbourhoodData | null => {
  const payload = narrowPayload(rawPayload);
  if (!payload) return null;

  const cityName = toTrimmedString(payload.city);
  const universities = selectUniversities(rawNearbyDistances);

  return {
    costOfLiving: transformCostOfLiving(
      payload.cost_of_living,
      countryCode,
      rentBudgetPercent,
      cityName,
    ),
    gettingAround: GETTING_AROUND_DATA,
    safety: transformSafety(payload.safety, cityName),
    community: transformCommunity(payload.community, universities, countryCode),
    climate: transformClimate(payload.climate, countryCode),
  };
};

export default buildNeighbourhoodData;
